import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from "@microsoft/signalr";
import { ChatComment } from "../models/ChatComment";
import { makeAutoObservable, runInAction } from "mobx";
import { store } from "./store";

export default class CommentStore {
    comments: ChatComment[] = [];
    hubConnection: HubConnection | null = null;
    onlineUsers: Set<string> = new Set();
    
    // Private state management
    private presenceInterval: NodeJS.Timeout | null = null;
    private isConnecting: boolean = false;
    private connectionRetryCount: number = 0;
    private readonly maxConnectionRetries: number = 3;
    private connectionRetryTimeout: NodeJS.Timeout | null = null;
    private currentGameId: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    // ========== PUBLIC METHODS ==========
    createHubConnection = async (gameID: string): Promise<void> => {
        // Don't reconnect to the same game
        if (this.currentGameId === gameID && this.hubConnection?.state === 'Connected') {
            console.log(`✅ Already connected to game ${gameID}, skipping`);
            return;
        }

        // Prevent multiple connection attempts
        if (this.isConnecting) {
            console.log('⚠️ Already connecting to comment hub, skipping');
            return;
        }

        this.currentGameId = gameID;
        this.isConnecting = true;
        
        console.log(`🎮 Creating comment hub connection for game: ${gameID}`);

        // Clean up existing connection
        await this.cleanupExistingConnection();

        // Reset retry count for new connection
        this.connectionRetryCount = 0;

        try {
            await this.buildAndStartConnection(gameID);
            console.log(`✅ Comment hub connected successfully for game ${gameID}`);
        } catch (error) {
            console.error(`❌ Comment hub connection failed for game ${gameID}:`, error);
            await this.handleConnectionFailure(gameID);
        }
    };

    stopHubConnection = () => {
        console.log('🛑 Stopping comment hub connection');
        
        // Cancel any pending retry
        this.cancelPendingRetry();
        
        // Reset connection state
        this.isConnecting = false;
        this.connectionRetryCount = 0;
        this.currentGameId = null;
        
        // Clear presence interval
        if (this.presenceInterval) {
            clearInterval(this.presenceInterval);
            this.presenceInterval = null;
        }

        // Stop connection
        this.hubConnection?.stop()
            .then(() => console.log('✅ Comment hub connection stopped'))
            .catch(error => console.log('Error stopping comment connection: ', error));
    };

    clearComments = () => {
        this.comments = [];
        this.onlineUsers.clear();
        this.stopHubConnection();
    };

    addComment = async (values: any) => {
        if (store.gameStore.selectedGame?.isGameInThePast) return;

        values.gameID = store.gameStore.selectedGame?.id;
        try {
            await this.safeHubInvoke('SendComment', values);
        } catch (error) {
            console.log('Error adding comment:', error);
        }
    };

    deleteComment = async (commentId: string) => {
        try {
            await this.safeHubInvoke('DeleteComment', { 
                CommentID: commentId
            });
        } catch (error) {
            console.log('Error deleting comment:', error);
            throw error;
        }
    };

    isUserOnline = (userId: string): boolean => {
        return this.onlineUsers.has(userId);
    };

    // ========== PRIVATE METHODS ==========
    private cleanupExistingConnection = async (): Promise<void> => {
        if (this.hubConnection && this.hubConnection.state !== 'Disconnected') {
            console.log('🛑 Stopping existing comment connection first');
            try {
                // Don't wait too long for stop to complete
                const stopPromise = this.hubConnection.stop();
                await Promise.race([
                    stopPromise,
                    new Promise(resolve => setTimeout(resolve, 300))
                ]);
            } catch (error) {
                // Ignore errors during stop
            }
        }
    };

    private cancelPendingRetry = () => {
        if (this.connectionRetryTimeout) {
            clearTimeout(this.connectionRetryTimeout);
            this.connectionRetryTimeout = null;
        }
    };

    private buildAndStartConnection = async (gameID: string): Promise<void> => {
        const token = store.userStore.user?.token;
        if (!token) {
            throw new Error('No authentication token available');
        }

        const hubUrl = process.env.NODE_ENV === 'production' 
            ? `/chat?gameID=${gameID}` 
            : `http://localhost:5000/chat?gameID=${gameID}`;

        this.hubConnection = new HubConnectionBuilder()
        
            .withUrl(hubUrl, {
                accessTokenFactory: () => token,
                skipNegotiation: false,
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    console.log(`🔁 Comment hub reconnect attempt ${retryContext.previousRetryCount}`);
                    // Quick retry for first attempts
                    if (retryContext.previousRetryCount < 2) return 1000;
                    return Math.min(2000 * Math.pow(1.5, retryContext.previousRetryCount), 15000);
                }
            })
            .configureLogging(LogLevel.Warning)
            .build();

        // Setup event handlers
        this.setupEventHandlers(gameID);

        // Start connection
        await this.hubConnection.start();
        
        this.isConnecting = false;
        
        // Send initial presence
        setTimeout(() => {
            this.hubConnection?.invoke('UpdatePresence', gameID)
                .then(() => console.log(`✅ Initial presence sent for game ${gameID}`))
                .catch(err => console.error('Error sending initial presence:', err));
        }, 300);

        // Setup presence heartbeat
        this.startPresenceInterval(gameID);
    };

    private setupEventHandlers = (gameID: string) => {
        if (!this.hubConnection) return;

        // Connection state handlers
        this.hubConnection.onreconnecting((error) => {
            console.log(`🔄 Comment hub reconnecting for game ${gameID}:`, error?.message || 'No error');
        });

        this.hubConnection.onreconnected(() => {
            console.log(`✅ Comment hub reconnected for game ${gameID}`);
            // Resend presence after reconnect
            setTimeout(() => {
                this.hubConnection?.invoke('UpdatePresence', gameID)
                    .catch(err => console.error('Error sending presence after reconnect:', err));
            }, 300);
        });

        this.hubConnection.onclose((error) => {
            console.log(`🔴 Comment hub closed for game ${gameID}:`, error?.message || 'Normal closure');
            this.isConnecting = false;
        });

        // Application event handlers
        this.hubConnection.on('LoadComments', this.handleLoadComments);
        this.hubConnection.on('UsersOnline', this.handleUsersOnline);

        // Only enable real-time updates if the game is in the future
        if (!store.gameStore.selectedGame?.isGameInThePast) {
            this.hubConnection.on('ReceiveComment', this.handleReceiveComment);
            this.hubConnection.on('DeleteComment', this.handleDeleteComment);
        }
    };

    private startPresenceInterval = (gameID: string) => {
        // Clear existing interval
        if (this.presenceInterval) {
            clearInterval(this.presenceInterval);
        }
        
        // Start new presence interval
        this.presenceInterval = setInterval(() => {
            if (this.hubConnection?.state === 'Connected') {
                this.hubConnection.invoke('UpdatePresence', gameID)
                    .catch(err => console.error('Error sending comment presence:', err));
            }
        }, 30000);
    };

    private handleConnectionFailure = async (gameID: string) => {
        this.isConnecting = false;
        
        this.connectionRetryCount++;
        
        if (this.connectionRetryCount >= this.maxConnectionRetries) {
            console.log(`🚫 Max comment hub retries reached for game ${gameID}, giving up`);
            this.connectionRetryCount = 0;
            return;
        }
        
        // Exponential backoff with jitter
        const baseDelay = 2000;
        const delay = Math.min(baseDelay * Math.pow(1.5, this.connectionRetryCount - 1), 10000);
        const jitter = delay * 0.2;
        const finalDelay = delay + (Math.random() * jitter * 2 - jitter);
        
        console.log(`🔄 Retrying comment hub in ${Math.round(finalDelay)}ms... (attempt ${this.connectionRetryCount})`);
        
        this.connectionRetryTimeout = setTimeout(() => {
            this.createHubConnection(gameID);
        }, finalDelay);
    };

    private safeHubInvoke = async <T>(methodName: string, ...args: any[]): Promise<T | null> => {
        const maxRetries = 2;
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                if (!this.hubConnection) {
                    throw new Error('Comment hub connection not initialized');
                }
                
                // Ensure connection is ready
                if (this.hubConnection.state !== 'Connected') {
                    await this.ensureConnectionReady();
                }
                
                return await this.hubConnection.invoke<T>(methodName, ...args);
            } catch (error) {
                console.error(`❌ Comment hub invoke failed (attempt ${attempt + 1}):`, error);
                
                if (attempt === maxRetries) {
                    throw error;
                }
                
                // Wait before retry with exponential backoff
                await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
            }
        }
        
        return null;
    };

    private ensureConnectionReady = async (): Promise<boolean> => {
        if (!this.hubConnection || !this.currentGameId) {
            return false;
        }

        if (this.hubConnection.state === 'Connected') {
            return true;
        }

        const hubConnection = this.hubConnection;

        console.log('🔌 Comment hub not ready, state:', this.hubConnection.state);

        // If connecting or reconnecting, wait a bit
        if (this.hubConnection.state === 'Connecting' || this.hubConnection.state === 'Reconnecting') {
            await new Promise(resolve => setTimeout(resolve, 1000));
            return hubConnection.state === 'Connected';
        }

        // If disconnected, try to reconnect
        if (this.hubConnection.state === 'Disconnected') {
            console.log('🔄 Comment hub disconnected, attempting to reconnect...');
            await this.createHubConnection(this.currentGameId);
            await new Promise(resolve => setTimeout(resolve, 1500));
            return hubConnection.state === 'Connected';
        }

        return false;
    };

    // ========== EVENT HANDLERS ==========
    private handleLoadComments = (comments: ChatComment[]) => {
        runInAction(() => {
            comments.forEach(comment => {
                comment.createdAt = new Date(comment.createdAt);
            });
            this.comments = comments;
        });
    };

    private handleUsersOnline = (userIds: string[]) => {
        runInAction(() => {
            this.onlineUsers = new Set(userIds);
        });
    };

    private handleReceiveComment = (comment: ChatComment) => {
        runInAction(() => {
            comment.createdAt = new Date(comment.createdAt);
            this.comments.unshift(comment);
        });
    };

    private handleDeleteComment = (commentId: string) => {
        runInAction(() => {
            this.comments = this.comments.filter(c => c.id !== commentId);
        });
    };
}